import Logo from "@funded-here-interface/common/src/assets/logo.png";
import useGetUser from "@funded-here-interface/common/src/hooks/useGetUsers";
import { performLogoutCleanup } from "@funded-here-interface/common/src/services/standardizedLogout";
import { NavLink, ScrollArea, Text } from "@mantine/core";
import { IconLogout, IconUser, IconBuilding } from "@tabler/icons-react";
import { FC, ReactNode, useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import useLogoutMutation from "../../hooks/useLogout";
import { RootState, store, persistor } from "../../store";
import { investorResetActions } from "../../Utils/investorResetActions";
import { useStyles } from "./NavBar.styles";

export interface IMenuItem {
  label: string;
  path?: string;
  submenu?: ISubMenu[];
  icon?: ReactNode;
}

interface ISubMenu {
  label: string;
  path: string;
}

interface INavBar {
  menus?: IMenuItem[];
  canPassCondition?: boolean | null;
  embedded?: boolean;
}

const NavBar: FC<INavBar> = ({ menus, canPassCondition = true, embedded = false }) => {
  const { classes, cx } = useStyles();
  const queryClient = useQueryClient();

  const [activeSubMenu, setActiveSubMenu] = useState<number | null>(null);
  const navigation = useNavigate();
  const location = useLocation();
  const { token, userId, orgName, spvName } = useSelector((state: RootState) => state.auth);
  const getUserMutation = useGetUser();
  const [name, setName] = useState({ firstName: "", lastName: "" });

  const logoutMutation = useLogoutMutation();

  const getUserName = () => {
    getUserMutation.mutate(
      { token },
      {
        onSuccess: (data) => {
          const userDetail = data.user;
          setName({
            firstName: userDetail?.firstName,
            lastName: userDetail?.lastName,
          });
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      },
    );
  };

  useEffect(() => {
    getUserName();
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserves existing hook behavior; verify in follow-up audit
  }, []);

  // Auto-open submenu when current route matches a submenu path
  useEffect(() => {
    if (!menus) return;
    menus.forEach((menu, index) => {
      if (menu.submenu && isCurrentPage(getAllSubPaths(menu.submenu))) {
        setActiveSubMenu(index);
      }
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserves existing hook behavior; verify in follow-up audit
  }, [location.pathname]);

  const handleLogout = () => {
    logoutMutation.mutate(
      { token, userId },
      {
        onSuccess: () => {
          performLogoutCleanup({
            navigate: navigation,
            store,
            persistor,
            queryClient,
            resetActions: investorResetActions,
          });
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      },
    );
  };

  const toggleSubMenu = (index: number) => {
    setActiveSubMenu(activeSubMenu === index ? null : index);
  };

  const isCurrentPage = (paths: string[]) => {
    return paths.includes(location.pathname);
  };

  const getAllSubPaths = (subMenu: ISubMenu[]): string[] => {
    return subMenu.map((item) => item.path);
  };

  const organizationName = spvName ?? orgName ?? null;

  const items = menus?.map((item, index) => {
    if (item.submenu) {
      return (
        <NavLink
          key={index}
          label={item.label}
          className={classes.link}
          childrenOffset={20}
          leftSection={item.icon}
          opened={activeSubMenu === index}
          onClick={() => toggleSubMenu(index)}
        >
          {item.submenu.map((subItem, subIndex) => (
            <NavLink
              key={subIndex}
              label={subItem.label}
              component={Link}
              to={subItem.path}
              className={classes.link}
              active={subItem.path === location.pathname}
            />
          ))}
        </NavLink>
      );
    }

    if (!item.path) return null;

    return (
      <NavLink
        key={index}
        label={item.label}
        component={Link}
        to={item.path}
        className={classes.link}
        leftSection={item.icon}
        active={item.path === location.pathname}
      />
    );
  });

  return (
    <div className={embedded ? classes.embeddedWrapper : classes.wrapper}>
      <nav className={cx(classes.sideBarContainer, embedded && classes.embedded)}>
        <div className={cx(classes.iconbox, embedded && classes.iconboxEmbedded)}>
          <img className={classes.logo} alt="LOGO" src={Logo} />
        </div>

        <ScrollArea className={classes.scrollArea}>
          {canPassCondition && <div>{items}</div>}
        </ScrollArea>

        <div className={classes.footer}>
          {name.firstName !== "" && (
            <div className={classes.personItem}>
              <IconUser size="1.5rem" stroke={2} className={classes.personIcon} />
              <Text size="sm">
                {name.firstName} {name.lastName}
              </Text>
            </div>
          )}

          {organizationName && (
            <div className={classes.personItem}>
              <IconBuilding size="1.5rem" stroke={2} className={classes.personIcon} />
              <Text size="sm">
                {organizationName.charAt(0).toUpperCase() + organizationName.slice(1)}
              </Text>
            </div>
          )}

          <NavLink
            label="Logout"
            leftSection={<IconLogout size="1.5rem" stroke={2} />}
            onClick={handleLogout}
            className={classes.link}
          />
        </div>
      </nav>
    </div>
  );
};

export default NavBar;
